"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IdentityRejectRequest } from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

// Admin review queue for account identity (CCCD) — SCRUM-71.
export function useAdminIdentityList(
  query: { page?: number; limit?: number } = {},
) {
  return useQuery({
    queryKey: [...qk.admin.identity, query] as const,
    queryFn: () => getSdk().admin.listIdentity(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminIdentity(userId: string | null | undefined) {
  return useQuery({
    queryKey: qk.admin.identityDetail(userId ?? ""),
    queryFn: () => getSdk().admin.getIdentity(userId!),
    enabled: !!userId,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
}

export function useApproveIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => getSdk().admin.approveIdentity(userId),
    onSuccess: (_d, userId) => {
      qc.invalidateQueries({ queryKey: qk.admin.identity });
      qc.invalidateQueries({ queryKey: qk.admin.identityDetail(userId) });
      qc.invalidateQueries({ queryKey: qk.admin.dashboard });
    },
  });
}

export function useRejectIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      body,
    }: {
      userId: string;
      body: IdentityRejectRequest;
    }) => getSdk().admin.rejectIdentity(userId, body),
    onSuccess: (_d, { userId }) => {
      qc.invalidateQueries({ queryKey: qk.admin.identity });
      qc.invalidateQueries({ queryKey: qk.admin.identityDetail(userId) });
    },
  });
}
